import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Search, Trash2, Mail, Phone, ExternalLink, ArrowLeft, RefreshCw, Globe, UserPlus, X, CheckCircle2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { adminAuditEnquiries, deleteAuditEnquiry, convertEnquiryToLead, getAutoDeckSettings, setAutoDeckSettings } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export default function AdminEnquiries() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const load = useCallback((query = "") => {
    setData(null);
    adminAuditEnquiries(query).then(setData).catch(() => setData({ enquiries: [], stats: {} }));
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const del = async (id, domain) => {
    if (!window.confirm(`Delete enquiry for ${domain}?`)) return;
    try { await deleteAuditEnquiry(id); toast.success("Deleted"); load(q); } catch { toast.error("Delete failed"); }
  };

  const [convertTarget, setConvertTarget] = useState(null);
  const [form, setForm] = useState({ city: "", district: "", name: "", email: "", phone: "", note: "" });
  const [converting, setConverting] = useState(false);

  const [autoDeck, setAutoDeck] = useState(null);
  useEffect(() => { if (user?.role === "admin") getAutoDeckSettings().then(setAutoDeck).catch(() => {}); }, [user]);
  const saveAutoDeck = async (patch) => {
    const prev = autoDeck;
    setAutoDeck({ ...autoDeck, ...patch });
    try { setAutoDeck(await setAutoDeckSettings(patch)); toast.success("Auto-deck settings saved"); }
    catch { setAutoDeck(prev); toast.error("Could not save settings"); }
  };

  const openConvert = (e) => {
    setForm({
      city: "", district: "", name: e.lead_name || e.domain || "",
      email: (e.submitted_emails || [])[0] || (e.site_emails || [])[0] || "",
      phone: (e.site_phones || [])[0] || "", note: "",
    });
    setConvertTarget(e);
  };

  const submitConvert = async () => {
    setConverting(true);
    try {
      const r = await convertEnquiryToLead(convertTarget.id, form);
      toast.success(r.routed_partner ? `Kerala lead created — routed to ${r.routed_partner}` : "Kerala lead created (no partner in that area yet)");
      setConvertTarget(null);
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert to lead");
    } finally { setConverting(false); }
  };

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const stats = data?.stats || {};
  const rows = data?.enquiries || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-enquiries">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand"><ArrowLeft size={16} /> Console</Link>
        <span className="text-sm font-semibold text-slate-700">Audit Enquiries</span>
        <button onClick={() => load(q)} className="text-slate-400 hover:text-brand" data-testid="enquiries-refresh"><RefreshCw size={16} /></button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Website Audit Enquiries</h1>
        <p className="text-slate-500 text-sm mt-1">Everyone who tested their website with the audit tools — with any contact info scraped from their site.</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {[["Businesses", stats.total], ["Total scans", stats.total_scans], ["With site contact", stats.with_site_contact], ["Requested report", stats.with_submitted]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-2xl p-4 card-shadow">
              <p className="text-xs uppercase tracking-wide text-slate-400">{l}</p>
              <p className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{v ?? 0}</p>
            </div>
          ))}
        </div>

        {autoDeck && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 card-shadow" data-testid="autodeck-settings">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-brand"><Wand2 size={17} /></span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">Auto redesign-preview emails</p>
                    <Switch checked={!!autoDeck.enabled} onCheckedChange={(v) => saveAutoDeck({ enabled: v })} data-testid="autodeck-toggle" />
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                    When a site scores below the threshold and we have a contact, we auto-generate an AI before/after redesign deck and email the prospect a “here’s what your site could look like” preview. Runs in the background, once per domain.
                  </p>
                </div>
              </div>
              <div className="flex items-end gap-3">
                <label className="text-xs text-slate-500">Score below
                  <input type="number" min="1" max="100" value={autoDeck.score_threshold}
                    onChange={(e) => setAutoDeck({ ...autoDeck, score_threshold: +e.target.value })}
                    onBlur={(e) => saveAutoDeck({ score_threshold: Math.max(1, Math.min(100, +e.target.value || 50)) })}
                    data-testid="autodeck-threshold" className="mt-1 block w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand" />
                </label>
                <label className="text-xs text-slate-500">Daily cap
                  <input type="number" min="0" max="500" value={autoDeck.daily_cap}
                    onChange={(e) => setAutoDeck({ ...autoDeck, daily_cap: +e.target.value })}
                    onBlur={(e) => saveAutoDeck({ daily_cap: Math.max(0, Math.min(500, +e.target.value || 0)) })}
                    data-testid="autodeck-cap" className="mt-1 block w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand" />
                </label>
                <div className="text-xs text-slate-500">Sent today<p className="mt-1 font-heading text-lg font-bold text-slate-900" data-testid="autodeck-sent-today">{autoDeck.sent_today ?? 0}</p></div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mt-6 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by domain or email…" data-testid="enquiries-search"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand" />
          </div>
          <button type="submit" className="bg-brand text-white font-semibold rounded-lg px-5 text-sm">Search</button>
        </form>

        {data === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={24} /></div>
        ) : rows.length === 0 ? (
          <p className="text-slate-500 text-center py-16">No enquiries yet. They appear whenever someone runs a website audit.</p>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Website</th><th className="text-left p-3">Score</th>
                  <th className="text-left p-3">Site contacts</th><th className="text-left p-3">Requested report</th>
                  <th className="text-left p-3">Scans</th><th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Last seen</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 align-top" data-testid="enquiry-row">
                    <td className="p-3">
                      <a href={e.last_url} target="_blank" rel="noreferrer" className="font-semibold text-slate-800 hover:text-brand inline-flex items-center gap-1"><Globe size={13} /> {e.domain} <ExternalLink size={11} /></a>
                    </td>
                    <td className="p-3"><span className={`font-bold ${e.last_score >= 70 ? "text-emerald-600" : e.last_score >= 45 ? "text-amber-600" : "text-rose-600"}`}>{e.last_score ?? "—"}</span></td>
                    <td className="p-3 text-slate-600">
                      {(e.site_emails || []).map((m) => <div key={m} className="inline-flex items-center gap-1 text-xs"><Mail size={11} className="text-slate-400" /> {m}</div>)}
                      {(e.site_phones || []).map((p) => <div key={p} className="inline-flex items-center gap-1 text-xs"><Phone size={11} className="text-slate-400" /> {p}</div>)}
                      {!(e.site_emails || []).length && !(e.site_phones || []).length && <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="p-3 text-slate-600 text-xs">{(e.submitted_emails || []).join(", ") || <span className="text-slate-300">—</span>}{e.lead_name ? <div className="text-slate-400">{e.lead_name}</div> : null}</td>
                    <td className="p-3 text-slate-600">{e.scan_count || 1}</td>
                    <td className="p-3 text-slate-400 text-xs">{e.last_source || "—"}</td>
                    <td className="p-3 text-slate-400 text-xs">{(e.updated_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {e.converted_lead_id ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700" data-testid="enquiry-converted-badge" title={e.routed_partner_name ? `Routed to ${e.routed_partner_name}` : "Lead created"}>
                            <CheckCircle2 size={12} /> Lead{e.routed_partner_name ? ` · ${e.routed_partner_name}` : ""}
                          </span>
                        ) : (
                          <button onClick={() => openConvert(e)} data-testid="enquiry-convert" title="Convert to Kerala lead"
                            className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                            <UserPlus size={13} /> To lead
                          </button>
                        )}
                        <button onClick={() => del(e.id, e.domain)} data-testid="enquiry-delete" className="text-rose-400 hover:bg-rose-50 rounded-lg p-2"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {convertTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="convert-modal" onClick={() => !converting && setConvertTarget(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 card-shadow" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Convert to Kerala lead</h3>
                <p className="text-xs text-slate-400 mt-0.5">{convertTarget.domain} · score {convertTarget.last_score ?? "—"}</p>
              </div>
              <button onClick={() => setConvertTarget(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mt-3">Add a city/district to auto-route this lead to the right City Partner. Leave blank to create an unrouted lead you can assign later.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City (e.g. Kochi)" data-testid="convert-city" className="col-span-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder="District (e.g. Ernakulam)" data-testid="convert-district" className="col-span-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lead name" data-testid="convert-name" className="col-span-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="convert-email" className="col-span-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="convert-phone" className="col-span-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Note (optional)" data-testid="convert-note" className="col-span-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConvertTarget(null)} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
              <button onClick={submitConvert} disabled={converting} data-testid="convert-submit" className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {converting ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Create & route lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
